import axios from "axios";
import { BASE_URL } from "../url";

const DEFAULT_CLIENT_LOG_PATH = "/v1/clientLogs?api-version=1";
const SESSION_STORAGE_KEY = "vim_client_log_session_id";
const MAX_LOGS_PER_PAGE = 80;
const DUPLICATE_WINDOW_MS = 5000;
const MAX_TEXT_LENGTH = 2000;

let initialized = false;
let originalFetch = null;
let logCount = 0;
const recentLogFingerprints = {};

const truncate = (value, maxLength = MAX_TEXT_LENGTH) => {
  if (value === undefined || value === null) {
    return value;
  }

  const text = String(value);
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const getLogEndpoint = () => {
  const configuredEndpoint = process.env.REACT_APP_CLIENT_LOG_ENDPOINT;

  if (configuredEndpoint && configuredEndpoint.trim()) {
    return configuredEndpoint.trim();
  }

  if (BASE_URL.API) {
    return `${String(BASE_URL.API).replace(/\/$/, "")}${DEFAULT_CLIENT_LOG_PATH}`;
  }

  return "";
};

const getStorageValue = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const getSessionId = () => {
  try {
    const existingSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existingSessionId) {
      return existingSessionId;
    }

    const newSessionId = `${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, newSessionId);
    return newSessionId;
  } catch (error) {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
};

const parseUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    return new URL(String(url), window.location.origin);
  } catch (error) {
    return null;
  }
};

const sanitizeUrl = (url) => {
  const parsedUrl = parseUrl(url);

  if (!parsedUrl) {
    return truncate(String(url || "").split("?")[0], 500);
  }

  return `${parsedUrl.origin}${parsedUrl.pathname}`;
};

const getServiceName = (url) => {
  const parsedUrl = parseUrl(url);

  if (!parsedUrl) {
    return "unknown";
  }

  if (parsedUrl.hostname.includes("logic.azure.com")) {
    return "power-automate";
  }

  const apiUrl = parseUrl(BASE_URL.API);
  if (apiUrl && parsedUrl.origin === apiUrl.origin) {
    return "backend-api";
  }

  return "external";
};

const getPagePath = () => {
  try {
    return `${window.location.pathname}${window.location.hash}`;
  } catch (error) {
    return "";
  }
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "";
  }
};

const getBrowserContext = () => ({
  app: "VIM_UI",
  environment: process.env.NODE_ENV,
  timestamp: new Date().toISOString(),
  sessionId: getSessionId(),
  pagePath: getPagePath(),
  userEmail: getStorageValue("email"),
  language: navigator.language,
  userAgent: navigator.userAgent,
  online: navigator.onLine,
  timezone: getTimezone(),
});

const serializeError = (error) => {
  if (!error) {
    return {};
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: truncate(error.message),
      stack: truncate(error.stack),
    };
  }

  if (typeof error === "string") {
    return { message: truncate(error) };
  }

  return {
    name: truncate(error.name),
    message: truncate(error.message || safeStringify(error)),
    stack: truncate(error.stack),
  };
};

const shouldSkipLogUrl = (url) => {
  const logEndpoint = getLogEndpoint();
  if (!url || !logEndpoint) {
    return false;
  }

  return sanitizeUrl(url) === sanitizeUrl(logEndpoint);
};

const shouldSendLog = (event) => {
  if (logCount >= MAX_LOGS_PER_PAGE) {
    return false;
  }

  const fingerprint = [
    event.eventType,
    event.error && event.error.message,
    event.request && event.request.method,
    event.request && event.request.url,
    event.response && event.response.status,
  ].join("|");

  const now = Date.now();
  if (
    recentLogFingerprints[fingerprint] &&
    now - recentLogFingerprints[fingerprint] < DUPLICATE_WINDOW_MS
  ) {
    return false;
  }

  recentLogFingerprints[fingerprint] = now;
  return true;
};

const postLog = (event) => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return;
  }

  const logEndpoint = getLogEndpoint();
  if (!logEndpoint || !shouldSendLog(event)) {
    return;
  }

  logCount += 1;

  const payload = JSON.stringify({
    ...getBrowserContext(),
    ...event,
  });

  try {
    if (navigator.sendBeacon && payload.length < 60000) {
      const sent = navigator.sendBeacon(
        logEndpoint,
        new Blob([payload], { type: "application/json" })
      );
      if (sent) {
        return;
      }
    }
  } catch (error) {
    // Fall back to fetch below.
  }

  const fetcher = originalFetch || window.fetch;
  if (!fetcher) {
    return;
  }

  try {
    fetcher(logEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch (error) {
    // Logging must never affect the application flow.
  }
};

export const logClientError = (error, context = {}) => {
  const event = {
    eventType: context.eventType || "client_error",
    error: serializeError(error),
    ...context,
  };

  postLog(event);
};

const getAxiosRequestUrl = (config) => {
  if (!config) {
    return "";
  }

  if (!config.baseURL) {
    return config.url || "";
  }

  try {
    return new URL(config.url || "", config.baseURL).toString();
  } catch (error) {
    return config.url || "";
  }
};

const getHeaderNames = (headers) => {
  if (!headers || typeof headers !== "object") {
    return [];
  }

  return Object.keys(headers).filter(Boolean);
};

const logAxiosError = (error) => {
  if (axios.isCancel && axios.isCancel(error)) {
    return;
  }

  const config = error && error.config ? error.config : {};
  const requestUrl = getAxiosRequestUrl(config);

  if (shouldSkipLogUrl(requestUrl)) {
    return;
  }

  const startedAt = config.clientLoggerStartedAt;
  const durationMs = startedAt ? Date.now() - startedAt : undefined;
  const status = error && error.response ? error.response.status : undefined;

  logClientError(error, {
    eventType: "api_request_failed",
    request: {
      method: (config.method || "GET").toUpperCase(),
      url: sanitizeUrl(requestUrl),
      service: getServiceName(requestUrl),
      durationMs,
      headerNames: getHeaderNames(config.headers),
    },
    response: {
      status,
      statusText: error && error.response ? error.response.statusText : undefined,
    },
    diagnostics: {
      axiosCode: error && error.code,
      networkError: Boolean(error && !error.response),
      corsOrConnectivityLikely: Boolean(error && !error.response && navigator.onLine),
    },
  });
};

export const attachAxiosLogger = (axiosClient) => {
  if (
    !axiosClient ||
    !axiosClient.interceptors ||
    axiosClient.__clientLoggerAttached
  ) {
    return;
  }

  axiosClient.__clientLoggerAttached = true;

  axiosClient.interceptors.request.use(
    (config) => ({
      ...config,
      clientLoggerStartedAt: Date.now(),
    }),
    (error) => {
      logClientError(error, { eventType: "api_request_setup_failed" });
      return Promise.reject(error);
    }
  );

  axiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
      logAxiosError(error);
      return Promise.reject(error);
    }
  );
};

const getFetchRequestInfo = (input, init = {}) => {
  const requestUrl =
    typeof input === "string" ? input : input && input.url ? input.url : "";
  const requestMethod =
    init.method || (input && input.method ? input.method : "GET");

  return {
    method: String(requestMethod || "GET").toUpperCase(),
    url: requestUrl,
  };
};

const patchFetch = () => {
  if (!window.fetch || window.fetch.__clientLoggerAttached) {
    return;
  }

  originalFetch = window.fetch.bind(window);

  const patchedFetch = (input, init = {}) => {
    const requestInfo = getFetchRequestInfo(input, init);

    return originalFetch(input, init)
      .then((response) => {
        if (!response.ok && !shouldSkipLogUrl(requestInfo.url)) {
          logClientError(new Error(`Fetch request failed with ${response.status}`), {
            eventType: "fetch_request_failed",
            request: {
              method: requestInfo.method,
              url: sanitizeUrl(requestInfo.url),
              service: getServiceName(requestInfo.url),
            },
            response: {
              status: response.status,
              statusText: response.statusText,
            },
          });
        }

        return response;
      })
      .catch((error) => {
        if (!shouldSkipLogUrl(requestInfo.url)) {
          logClientError(error, {
            eventType: "fetch_request_failed",
            request: {
              method: requestInfo.method,
              url: sanitizeUrl(requestInfo.url),
              service: getServiceName(requestInfo.url),
            },
            diagnostics: {
              networkError: true,
              corsOrConnectivityLikely: navigator.onLine,
            },
          });
        }

        throw error;
      });
  };

  patchedFetch.__clientLoggerAttached = true;
  window.fetch = patchedFetch;
};

const patchConsoleError = () => {
  if (
    !window.console ||
    !window.console.error ||
    window.console.error.__clientLoggerAttached
  ) {
    return;
  }

  const originalConsoleError = window.console.error.bind(window.console);

  const patchedConsoleError = (...args) => {
    originalConsoleError(...args);

    const firstError = args.find((arg) => arg instanceof Error);
    const message = args
      .map((arg) =>
        arg instanceof Error ? arg.message : truncate(safeStringify(arg) || arg)
      )
      .join(" ");

    logClientError(firstError || new Error(message), {
      eventType: "console_error",
    });
  };

  patchedConsoleError.__clientLoggerAttached = true;
  window.console.error = patchedConsoleError;
};

const addGlobalErrorHandlers = () => {
  window.addEventListener(
    "error",
    (event) => {
      const resourceUrl =
        event.target && (event.target.src || event.target.href)
          ? event.target.src || event.target.href
          : "";

      if (resourceUrl) {
        logClientError(new Error("Resource failed to load"), {
          eventType: "resource_load_failed",
          request: {
            url: sanitizeUrl(resourceUrl),
            service: getServiceName(resourceUrl),
          },
        });
        return;
      }

      logClientError(event.error || new Error(event.message), {
        eventType: "uncaught_error",
        source: {
          file: sanitizeUrl(event.filename),
          lineNumber: event.lineno,
          columnNumber: event.colno,
        },
      });
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    logClientError(event.reason || new Error("Unhandled promise rejection"), {
      eventType: "unhandled_promise_rejection",
    });
  });
};

export const initClientLogger = () => {
  if (initialized || typeof window === "undefined") {
    return;
  }

  initialized = true;

  attachAxiosLogger(axios);
  patchFetch();
  patchConsoleError();
  addGlobalErrorHandlers();
};
